import type { OrgConfigDef, PageSnapshot } from "./api";

export const SNAPSHOT_KINDS = ["landing", "login", "results-in", "results-out"] as const;
export type SnapshotKind = (typeof SNAPSHOT_KINDS)[number];

const KIND_LABELS: Record<SnapshotKind, string> = {
  landing: "Landing",
  login: "Login page",
  "results-in": "Results (logged in)",
  "results-out": "Results (logged out)",
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind as SnapshotKind] ?? kind;
}

export interface KnownVendor {
  id: string;
  name: string;
}

/**
 * The suppliers that really exist, folded out of the per-org config definitions —
 * one entry per config id, named from the org's own definition. There is no shared
 * vendor list to read: an id defined by several orgs collapses to one entry here.
 */
export function knownVendors(defs: OrgConfigDef[]): KnownVendor[] {
  const byId = new Map<string, KnownVendor>();
  for (const d of defs) {
    const name = d.config?.name?.trim() || d.configId;
    const existing = byId.get(d.configId);
    if (!existing || existing.name === d.configId) byId.set(d.configId, { id: d.configId, name });
  }
  return [...byId.values()];
}

export interface SupplierSnapshots {
  vendorId: string;
  vendorName: string;
  byKind: Partial<Record<SnapshotKind, PageSnapshot>>;
  missing: SnapshotKind[];
}

/**
 * One group per supplier, sorted by name. Suppliers come from the orgs' own config
 * definitions (so a vendor with ZERO captures still shows, all kinds missing) plus
 * any vendor that only appears in snapshots (e.g. a config since removed). Snapshots
 * arrive newest-first from the API; the first row per (vendor, kind) wins.
 */
export function groupSnapshots(
  snapshots: PageSnapshot[],
  vendors: KnownVendor[] = [],
): SupplierSnapshots[] {
  const groups = new Map<string, SupplierSnapshots>();
  const ensure = (vendorId: string, vendorName: string): SupplierSnapshots => {
    let g = groups.get(vendorId);
    if (!g) {
      g = { vendorId, vendorName, byKind: {}, missing: [] };
      groups.set(vendorId, g);
    }
    return g;
  };
  for (const v of vendors) ensure(v.id, v.name || v.id);
  for (const s of snapshots) {
    const g = ensure(s.vendor_id, s.vendor_name || s.vendor_id);
    const kind = s.page_kind as SnapshotKind;
    if (!SNAPSHOT_KINDS.includes(kind)) continue;
    if (!g.byKind[kind]) g.byKind[kind] = s;
  }
  for (const g of groups.values()) {
    g.missing = SNAPSHOT_KINDS.filter((k) => !g.byKind[k]);
  }
  return [...groups.values()].sort((a, b) =>
    a.vendorName.localeCompare(b.vendorName, undefined, { sensitivity: "base" }),
  );
}

export interface SnapshotFilters {
  /** "" = all kinds. */
  kind: SnapshotKind | "";
  /** With a kind: suppliers missing THAT kind. Without: suppliers missing anything. */
  missingOnly: boolean;
  /** Substring match on vendor id or name. */
  query: string;
}

export function filterGroups(
  groups: SupplierSnapshots[],
  f: SnapshotFilters,
): SupplierSnapshots[] {
  const q = f.query.trim().toLowerCase();
  return groups.filter((g) => {
    if (q && !g.vendorId.toLowerCase().includes(q) && !g.vendorName.toLowerCase().includes(q))
      return false;
    if (f.kind && f.missingOnly) return g.missing.includes(f.kind);
    if (f.kind) return !!g.byKind[f.kind];
    if (f.missingOnly) return g.missing.length > 0;
    return true;
  });
}
